import { createSlice } from '@reduxjs/toolkit';
import {
  getAllCartItems,
  getAllCartItemsCount,
  insertItemToCart,
  insertTicketBulkToCart,
  removeItemFromCart
} from './cartServices';

const initialState = {
  allCartItems: {
    isCartItemsLoading: null,
    isCartItemsSuccess: null,
    cartMessage: null,
    cartItems: []
  },
  insertToCart: {
    isCartItemCreating: null,
    isCartItemSuccess: null,
    cartItemMessage: null,
  },
  deleteFromCart: {
    isCartItemDeleting: null,
    isDeletetemSuccess: null,
    cartItemDeleteMessage: null,
  },
  insertBulkToCart: {
    isBulkCreating: null,
    isBulkSuccess: null,
    bulkMessage: null,
  },
  itemCount: { count: 0 },
  isItemCountLoading: false,
  shouldRefreshCount: false,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setShouldRefreshCount: (state, action) => {
      state.shouldRefreshCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllCartItems.pending, (state) => {
        state.allCartItems = {
          ...initialState.allCartItems,
          isCartItemsLoading: true,
          cartMessage: 'Loading carts...',
        };
      })
      .addCase(getAllCartItems.rejected, (state, action) => {
        state.allCartItems = {
          ...initialState.allCartItems,
          isCartItemsLoading: false,
          isCartItemsSuccess: false,
          cartMessage: action.payload?.message || 'Failed to load cart items',
        };
      })
      .addCase(getAllCartItems.fulfilled, (state, action) => {
        state.allCartItems = {
          ...initialState.allCartItems,
          isCartItemsLoading: false,
          isCartItemsSuccess: true,
          cartMessage: action.payload?.message || 'Cart items loaded',
          cartItems: action.payload,
        };
      })

      // Insert item
      .addCase(insertItemToCart.pending, (state) => {
        state.insertToCart = {
          ...initialState.insertToCart,
          isCartItemCreating: true,
          cartItemMessage: 'Inserting item to the cart...',
        };
      })
      .addCase(insertItemToCart.rejected, (state, action) => {
        state.insertToCart = {
          ...initialState.insertToCart,
          isCartItemCreating: false,
          isCartItemSuccess: false,
          cartItemMessage: action.payload?.message || 'Insert failed',
        };
      })
      .addCase(insertItemToCart.fulfilled, (state, action) => {
        state.insertToCart = {
          ...initialState.insertToCart,
          isCartItemCreating: false,
          isCartItemSuccess: true,
          cartItemMessage: action.payload?.message || 'Item inserted',
        };
      })

      // Remove item
      .addCase(removeItemFromCart.pending, (state) => {
        state.deleteFromCart = {
          ...initialState.deleteFromCart,
          isCartItemDeleting: true,
          cartItemDeleteMessage: 'Removing the item...',
        };
      })
      .addCase(removeItemFromCart.rejected, (state, action) => {
        state.deleteFromCart = {
          ...initialState.deleteFromCart,
          isCartItemDeleting: false,
          isDeletetemSuccess: false,
          cartItemDeleteMessage: action.payload?.message || 'Remove failed',
        };
      })
      .addCase(removeItemFromCart.fulfilled, (state, action) => {
        state.deleteFromCart = {
          ...initialState.deleteFromCart,
          isCartItemDeleting: false,
          isDeletetemSuccess: true,
          cartItemDeleteMessage: action.payload?.message || 'Item removed',
        };
      })

      // Bulk insert
      .addCase(insertTicketBulkToCart.pending, (state) => {
        state.insertBulkToCart = {
          ...initialState.insertBulkToCart,
          isBulkCreating: true,
          bulkMessage: 'Bulk inserting...',
        };
      })
      .addCase(insertTicketBulkToCart.rejected, (state, action) => {
        state.insertBulkToCart = {
          ...initialState.insertBulkToCart,
          isBulkCreating: false,
          isBulkSuccess: false,
          bulkMessage: action.payload?.message || 'Bulk insert failed',
        };
      })
      .addCase(insertTicketBulkToCart.fulfilled, (state, action) => {
        state.insertBulkToCart = {
          ...initialState.insertBulkToCart,
          isBulkCreating: false,
          isBulkSuccess: true,
          bulkMessage: action.payload?.message || 'Bulk insert success',
        };
      })

      // Cart count
      .addCase(getAllCartItemsCount.pending, (state) => {
        state.isItemCountLoading = true;
      })
      .addCase(getAllCartItemsCount.fulfilled, (state, action) => {
        state.isItemCountLoading = false;
        state.itemCount = action.payload;
        state.shouldRefreshCount = false;
      })
      .addCase(getAllCartItemsCount.rejected, (state) => {
        state.isItemCountLoading = false;
      });
  },
});

export const { setShouldRefreshCount } = cartSlice.actions;
export default cartSlice.reducer;
